import React, { useState } from 'react';

const COLORS = {
  primary: '#C41E3A',
  secondary: '#2D3748',
  accent: '#FFD700',
  success: '#38A169',
  error: '#E53E3E',
  bg: '#F7FAFC',
  white: '#FFFFFF',
  gray100: '#F7FAFC',
  gray200: '#EDF2F7',
  gray300: '#E2E8F0',
  gray400: '#CBD5E0',
  gray500: '#A0AEC0',
  gray600: '#718096',
  gray700: '#4A5568',
  gray800: '#2D3748',
  gray900: '#1A202C',
};

const FONTS = {
  heading: "'Poppins', sans-serif",
  body: "'Inter', sans-serif",
  number: "'Montserrat', sans-serif",
};

const screens = [
  { group: 'Coach App', items: [
    { id: 'coach-dashboard', label: 'Dashboard' },
    { id: 'coach-qr', label: 'Live Attendance' },
    { id: 'coach-exercise', label: 'Exercises' },
    { id: 'coach-earnings', label: 'Earnings' },
    { id: 'coach-schedule', label: 'Schedule' },
    { id: 'coach-leave', label: 'Leave' },
    { id: 'coach-profile', label: 'Profile' },
    { id: 'coach-payments', label: 'Payments' },
  ]},
  { group: 'Student App', items: [
    { id: 'student-landing', label: 'Landing' },
    { id: 'student-signup', label: 'Sign Up' },
    { id: 'student-otp', label: 'OTP' },
    { id: 'student-login', label: 'Login' },
    { id: 'student-dashboard', label: 'Dashboard' },
    { id: 'parent-view', label: 'Parent View' },
    { id: 'student-qr', label: 'QR Scan' },
    { id: 'student-progress', label: 'Progress' },
    { id: 'student-exercises', label: 'Exercises' },
    { id: 'student-payments', label: 'Payments' },
    { id: 'student-profile', label: 'Profile' },
    { id: 'add-student-dialog', label: 'Add Student' },
  ]},
];

/* ── Shared sub-components ─────────────────────────────── */

function StatusBar() {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px 4px', fontSize: 11, fontFamily: FONTS.number, fontWeight: 600, color: COLORS.gray800 }}>
      <span>9:41</span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <svg width="14" height="10" viewBox="0 0 14 10"><rect x="0" y="6" width="2.5" height="4" rx="0.5" fill={COLORS.gray800}/><rect x="3.5" y="4" width="2.5" height="6" rx="0.5" fill={COLORS.gray800}/><rect x="7" y="2" width="2.5" height="8" rx="0.5" fill={COLORS.gray800}/><rect x="10.5" y="0" width="2.5" height="10" rx="0.5" fill={COLORS.gray800}/></svg>
        <svg width="14" height="10" viewBox="0 0 14 10"><path d="M7 1C4.5 1 2.3 2 0.7 3.6L2.1 5C3.4 3.7 5.1 2.9 7 2.9s3.6.8 4.9 2.1l1.4-1.4C11.7 2 9.5 1 7 1zm0 4c-1.4 0-2.6.5-3.5 1.4L5 7.8c.6-.5 1.3-.9 2-.9s1.4.3 2 .9l1.5-1.4C9.6 5.5 8.4 5 7 5zm0 3.5c-.6 0-1 .4-1 1s.4 1 1 1 1-.4 1-1-.4-1-1-1z" fill={COLORS.gray800}/></svg>
        <svg width="22" height="10" viewBox="0 0 22 10"><rect x="0" y="1" width="18" height="8" rx="1.5" stroke={COLORS.gray800} strokeWidth="1" fill="none"/><rect x="1.5" y="2.5" width="13" height="5" rx="0.5" fill={COLORS.success}/><rect x="19" y="3" width="2" height="4" rx="0.5" fill={COLORS.gray800}/></svg>
      </div>
    </div>
  );
}

function BottomNav({ items, active }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', padding: '8px 0 6px', borderTop: `1px solid ${COLORS.gray200}`, background: COLORS.white }}>
      {items.map((item) => (
        <div key={item.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'pointer' }}>
          <span style={{ fontSize: 16 }}>{item.icon}</span>
          <span style={{ fontSize: 9, fontFamily: FONTS.body, fontWeight: active === item.label ? 700 : 400, color: active === item.label ? COLORS.primary : COLORS.gray500 }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function PhoneFrame({ children, style: extraStyle }) {
  return (
    <div style={{ background: '#000', borderRadius: 36, padding: 8, width: 320, flexShrink: 0, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', ...extraStyle }}>
      <div style={{ background: COLORS.white, borderRadius: 28, overflow: 'hidden', minHeight: 580, display: 'flex', flexDirection: 'column' }}>
        <StatusBar />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children, style: s }) {
  return (
    <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 700, color: COLORS.gray700, textTransform: 'uppercase', letterSpacing: 0.8, padding: '10px 16px 4px', ...s }}>{children}</div>
  );
}

function Badge({ label, color, bg }) {
  return (
    <span style={{ fontSize: 9, fontFamily: FONTS.body, fontWeight: 600, color: color || COLORS.white, background: bg || COLORS.primary, borderRadius: 8, padding: '2px 7px' }}>{label}</span>
  );
}

/* ── Screen renderers ──────────────────────────────────── */

function CoachDashboard() {
  const coachNav = [
    { icon: '🏠', label: 'Home' },
    { icon: '📅', label: 'Schedule' },
    { icon: '💰', label: 'Earnings' },
    { icon: '👤', label: 'Profile' },
  ];
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, padding: '18px 16px 14px', color: COLORS.white }}>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, opacity: 0.85 }}>Good Morning</div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700 }}>Coach Ahmed</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {[{ n: '32', l: 'Students' }, { n: '3', l: 'Today' }, { n: '520', l: 'BHD/Mo' }].map((s) => (
              <div key={s.l} style={{ flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 18, fontWeight: 700 }}>{s.n}</div>
                <div style={{ fontSize: 9, opacity: 0.8, fontFamily: FONTS.body }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Today's Sessions */}
        <SectionTitle>Today's Sessions</SectionTitle>
        {[
          { name: 'Kids Karate', time: '3:00-4:00 PM', status: 'Done', count: '12/15', color: COLORS.success },
          { name: 'Adult Karate', time: '5:00-6:00 PM', status: 'NEXT', count: '0/20', color: COLORS.primary },
          { name: 'Advanced Kata', time: '7:00-8:00 PM', status: 'Later', count: '0/8', color: COLORS.gray400 },
        ].map((s) => (
          <div key={s.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ width: 4, height: 36, borderRadius: 2, background: s.color, marginRight: 10 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>{s.name}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{s.time} &middot; {s.count}</div>
            </div>
            {s.status === 'Done' && <Badge label="Done" bg={COLORS.success} />}
            {s.status === 'NEXT' && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <div style={{ background: COLORS.gray200, color: COLORS.gray700, borderRadius: 8, padding: '5px 12px', fontSize: 10, fontFamily: FONTS.heading, fontWeight: 600, cursor: 'pointer' }}>View</div>
                <div style={{ background: 'transparent', color: COLORS.primary, borderRadius: 8, padding: '5px 8px', fontSize: 9, fontFamily: FONTS.body, fontWeight: 600, cursor: 'pointer', border: `1px solid ${COLORS.primary}` }}>Request Start</div>
              </div>
            )}
            {s.status === 'Later' && <span style={{ fontSize: 10, color: COLORS.gray400, fontFamily: FONTS.body }}>Later</span>}
          </div>
        ))}

        {/* Admin info */}
        <div style={{ margin: '6px 16px 4px', padding: '8px 12px', background: '#EBF8FF', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12 }}>ℹ️</span>
          <span style={{ fontFamily: FONTS.body, fontSize: 10, color: '#2B6CB0' }}>Sessions started by admin at reception</span>
        </div>

        {/* My Students */}
        <SectionTitle>My Students</SectionTitle>
        {[
          { name: 'Fahad A.', pct: '92%' },
          { name: 'Noor K.', pct: '78%' },
          { name: 'Ali H.', pct: '95%' },
        ].map((s) => (
          <div key={s.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ width: 32, height: 32, borderRadius: 16, background: COLORS.gray200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, marginRight: 10 }}>{s.name[0]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600 }}>{s.name}</div>
              <div style={{ fontFamily: FONTS.number, fontSize: 10, color: COLORS.success }}>{s.pct} attendance</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 14, background: COLORS.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, cursor: 'pointer' }} title="Assign Exercise">📝</div>
              <div style={{ width: 28, height: 28, borderRadius: 14, background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, cursor: 'pointer', color: COLORS.white }} title="WhatsApp">💬</div>
            </div>
          </div>
        ))}
        <div style={{ height: 8 }} />
      </div>
      <BottomNav items={coachNav} active="Home" />
    </PhoneFrame>
  );
}

function CoachQR() {
  const attendees = [
    { name: 'Fahad Al-Ahmed', present: true, method: 'QR' },
    { name: 'Ali Hassan', present: true, method: 'QR' },
    { name: 'Noor Khalid', present: true, method: 'Fingerprint' },
    { name: 'Omar Yusuf', present: true, method: 'QR' },
    { name: 'Layla Mahmoud', present: true, method: 'QR' },
    { name: 'Hassan Ibrahim', present: true, method: 'Manual' },
    { name: 'Sara Al-Farsi', present: true, method: 'QR' },
    { name: 'Yusuf Nasser', present: true, method: 'Fingerprint' },
    { name: 'Maryam Jaber', present: false },
    { name: 'Khalid Tariq', present: false },
    { name: 'Fatima Rashid', present: false },
  ];
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Phone 1 — QR Code Display */}
      <PhoneFrame>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: COLORS.gray900 }}>
          {/* Dark header */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid rgba(255,255,255,0.1)` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.white }}>Session QR Code</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray400 }}>Adult Karate &middot; 5:00–6:00 PM</div>
          </div>
          {/* QR block */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
            {/* QR code placeholder */}
            <div style={{ background: COLORS.white, borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: '0 0 0 4px rgba(255,255,255,0.1)' }}>
              {/* Simple pixel art QR representation */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(11, 14px)', gap: 1 }}>
                {[
                  1,1,1,1,1,1,1,0,1,0,1,
                  1,0,0,0,0,0,1,0,0,1,1,
                  1,0,1,1,1,0,1,0,1,0,0,
                  1,0,1,1,1,0,1,0,0,1,1,
                  1,0,1,1,1,0,1,0,1,0,1,
                  1,0,0,0,0,0,1,0,1,1,0,
                  1,1,1,1,1,1,1,0,1,0,1,
                  0,0,0,0,0,0,0,0,0,1,0,
                  1,0,1,1,0,1,1,0,1,0,1,
                  0,1,0,0,1,0,0,0,0,1,0,
                  1,1,1,1,0,0,1,0,1,0,1,
                ].map((v, i) => (
                  <div key={i} style={{ width: 14, height: 14, background: v ? COLORS.gray900 : 'transparent', borderRadius: 1 }} />
                ))}
              </div>
            </div>
            {/* Live counter */}
            <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: COLORS.accent }}>8</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray400 }}>Checked In</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.15)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: COLORS.white }}>20</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray400 }}>Total</div>
              </div>
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500, marginBottom: 24, textAlign: 'center' }}>
              Students scan this code to check in
            </div>
            {/* End Session button */}
            <button style={{ background: COLORS.error, color: COLORS.white, border: 'none', borderRadius: 12, padding: '12px 32px', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
              End Session
            </button>
          </div>
        </div>
      </PhoneFrame>

      {/* Phone 2 — Live Attendance Viewer */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Live Attendance</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>Adult Karate &middot; 8 of 20</div>
          </div>
          {/* Info banner */}
          <div style={{ margin: '10px 16px', background: '#EBF8FF', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
            <span style={{ fontFamily: FONTS.body, fontSize: 10, color: '#2B6CB0' }}>To manually mark attendance, contact admin or use the admin portal. Live check-ins shown below.</span>
          </div>
          {attendees.map((s) => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
              <div style={{ width: 30, height: 30, borderRadius: 15, background: s.present ? '#C6F6D5' : COLORS.gray200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, marginRight: 10, color: s.present ? COLORS.success : COLORS.gray500 }}>
                {s.present ? '✓' : s.name[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray800 }}>{s.name}</div>
                {s.present && s.method && (
                  <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray500 }}>via {s.method}</div>
                )}
              </div>
              {s.present ? (
                <span style={{ fontSize: 14, color: COLORS.success }}>✅</span>
              ) : (
                <span style={{ fontSize: 10, color: COLORS.gray400, fontFamily: FONTS.body }}>Waiting</span>
              )}
            </div>
          ))}
        </div>
      </PhoneFrame>
    </div>
  );
}

function CoachExercise() {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Assign Exercise Phone */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Assign Exercise</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {['From Library', 'Custom'].map((tab, i) => (
                <div key={tab} style={{ padding: '5px 14px', borderRadius: 20, fontSize: 10, fontFamily: FONTS.body, fontWeight: 600, cursor: 'pointer', background: i === 0 ? COLORS.primary : COLORS.gray200, color: i === 0 ? COLORS.white : COLORS.gray600 }}>{tab}</div>
              ))}
            </div>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Student dropdown */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Student</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Fahad Al-Ahmed</span>
                <span style={{ color: COLORS.gray400 }}>▾</span>
              </div>
            </div>
            {/* Exercise Name */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Exercise Name</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray400 }}>e.g. Kata Heian Shodan</div>
            </div>
            {/* Description */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Description</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray400, minHeight: 60 }}>Detailed instructions...</div>
            </div>
            {/* YouTube Link */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>YouTube Link (optional)</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray400 }}>https://youtube.com/...</div>
              {/* Preview */}
              <div style={{ marginTop: 8, background: COLORS.gray100, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ height: 90, background: COLORS.gray800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontSize: 16 }}>▶</div>
                </div>
                <div style={{ padding: '6px 8px', fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray600 }}>Video Preview</div>
              </div>
            </div>
            {/* Due Date */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Due Date</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray800, display: 'flex', justifyContent: 'space-between' }}>
                <span>Feb 22, 2026</span>
                <span style={{ color: COLORS.gray400 }}>📅</span>
              </div>
            </div>
            {/* Assign Button */}
            <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '12px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>Assign Exercise</div>
          </div>
        </div>
      </PhoneFrame>

      {/* Exercise Tracker Phone */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Exercise Tracker</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>All assigned exercises</div>
          </div>
          {[
            { name: 'Front Kick Drill', student: 'Fahad A.', due: 'Feb 18', done: true },
            { name: 'Basic Punch Combo', student: 'Ali H.', due: 'Feb 19', done: true },
            { name: 'Kata Heian Shodan', student: 'Fahad A.', due: 'Feb 22', done: false },
            { name: 'Roundhouse Combo', student: 'Noor K.', due: 'Feb 24', done: false },
            { name: 'Block & Counter', student: 'Omar Y.', due: 'Feb 25', done: false },
          ].map((ex) => (
            <div key={ex.name + ex.student} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${COLORS.gray100}`, opacity: ex.done ? 0.7 : 1 }}>
              <div style={{ width: 28, height: 28, borderRadius: 14, background: ex.done ? '#C6F6D5' : COLORS.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, fontSize: 12, color: ex.done ? COLORS.success : COLORS.gray400 }}>
                {ex.done ? '✓' : '○'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600, color: COLORS.gray800, textDecoration: ex.done ? 'line-through' : 'none' }}>{ex.name}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{ex.student} &middot; Due {ex.due}</div>
              </div>
              {ex.done ? (
                <Badge label="Done" bg={COLORS.success} />
              ) : (
                <Badge label="Pending" bg={COLORS.accent} color={COLORS.gray800} />
              )}
            </div>
          ))}
        </div>
      </PhoneFrame>

      {/* Exercise Library Phone */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Exercise Library</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>Select to assign</div>
          </div>

          {/* Discipline Filter Pills */}
          <div style={{ display: 'flex', gap: 6, padding: '10px 16px', overflowX: 'auto' }}>
            {['All', 'Karate', 'Boxing', 'BJJ', 'Kickboxing'].map((d, i) => (
              <div key={d} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 10, fontFamily: FONTS.body, fontWeight: 600, cursor: 'pointer', flexShrink: 0, background: i === 1 ? COLORS.primary : COLORS.gray200, color: i === 1 ? COLORS.white : COLORS.gray600 }}>{d}</div>
            ))}
          </div>

          {/* Exercise List */}
          {[
            { title: 'Front Kick Combo', discipline: 'Karate', level: 'Beginner', color: '#C41E3A' },
            { title: 'Jab-Cross-Hook', discipline: 'Boxing', level: 'Intermediate', color: '#3182CE' },
            { title: 'Triangle Choke', discipline: 'BJJ', level: 'Advanced', color: '#805AD5' },
            { title: 'Roundhouse Kick', discipline: 'Kickboxing', level: 'Intermediate', color: '#DD6B20' },
            { title: 'Kata Heian Shodan', discipline: 'Karate', level: 'Beginner', color: '#C41E3A' },
            { title: 'Guard Pass Drill', discipline: 'BJJ', level: 'Beginner', color: '#805AD5' },
          ].map((ex, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px 10px 0', borderBottom: `1px solid ${COLORS.gray100}`, overflow: 'hidden' }}>
              <div style={{ width: 4, height: 44, background: ex.color, borderRadius: '0 2px 2px 0', marginRight: 12, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 700, color: COLORS.gray800, marginBottom: 4 }}>{ex.title}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{ fontSize: 9, fontFamily: FONTS.body, fontWeight: 600, color: COLORS.gray600, background: COLORS.gray200, borderRadius: 6, padding: '2px 6px' }}>{ex.discipline}</span>
                  <span style={{ fontSize: 9, fontFamily: FONTS.body, fontWeight: 600, color: COLORS.gray600, background: COLORS.gray200, borderRadius: 6, padding: '2px 6px' }}>{ex.level}</span>
                </div>
              </div>
              <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 8, padding: '5px 10px', fontSize: 10, fontFamily: FONTS.heading, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Assign</div>
            </div>
          ))}
        </div>
      </PhoneFrame>
    </div>
  );
}

function CoachEarnings() {
  const [earningsView, setEarningsView] = useState('list');
  const [selectedSession, setSelectedSession] = useState(null);

  const sessions = [
    { date: 'Feb 18', name: 'Kids Karate', students: 12, earned: '18.000' },
    { date: 'Feb 18', name: 'Adult Karate', students: 18, earned: '27.000' },
    { date: 'Feb 17', name: 'Advanced Kata', students: 8, earned: '12.000' },
    { date: 'Feb 17', name: 'Kids Karate', students: 14, earned: '21.000' },
    { date: 'Feb 16', name: 'Adult Karate', students: 20, earned: '30.000' },
    { date: 'Feb 16', name: 'Kids Karate', students: 11, earned: '16.500' },
    { date: 'Feb 15', name: 'Advanced Kata', students: 7, earned: '10.500' },
  ];

  const detailStudents = [
    { name: 'Fahad Al-Ahmed', belt: 'Green', beltBg: '#C6F6D5', beltColor: '#38A169', amount: '1.500' },
    { name: 'Ali Hassan', belt: 'Blue', beltBg: '#BEE3F8', beltColor: '#3182CE', amount: '1.500' },
    { name: 'Noor Khalid', belt: 'Yellow', beltBg: '#FEFCBF', beltColor: '#D69E2E', amount: '1.500' },
    { name: 'Omar Yusuf', belt: 'Purple', beltBg: '#E9D8FD', beltColor: '#805AD5', amount: '1.500' },
    { name: 'Sara Al-Farsi', belt: 'White', beltBg: COLORS.gray200, beltColor: COLORS.gray600, amount: '1.500' },
  ];

  if (earningsView === 'detail' && selectedSession) {
    return (
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Back header */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span onClick={() => setEarningsView('list')} style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.primary, cursor: 'pointer', fontWeight: 600 }}>← Back</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>{selectedSession.name}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{selectedSession.date}</div>
              </div>
            </div>
          </div>
          {/* Summary strip */}
          <div style={{ background: COLORS.gray100, margin: '10px 16px', borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-around' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FONTS.number, fontSize: 16, fontWeight: 700, color: COLORS.success }}>{selectedSession.earned} BHD</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray500 }}>Total Earned</div>
            </div>
            <div style={{ width: 1, background: COLORS.gray300 }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FONTS.number, fontSize: 16, fontWeight: 700, color: COLORS.gray800 }}>{selectedSession.students}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray500 }}>Students</div>
            </div>
          </div>
          <SectionTitle>Student Breakdown</SectionTitle>
          {detailStudents.map((st, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
              <div style={{ width: 30, height: 30, borderRadius: 15, background: st.beltBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: st.beltColor, marginRight: 10 }}>{st.name[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{st.name}</div>
                <Badge label={st.belt} bg={st.beltBg} color={st.beltColor} />
              </div>
              <span style={{ fontSize: 12, color: COLORS.success, marginRight: 6 }}>✅</span>
              <div style={{ fontFamily: FONTS.number, fontSize: 12, fontWeight: 700, color: COLORS.success }}>{st.amount} <span style={{ fontSize: 9, color: COLORS.gray500, fontWeight: 400 }}>BHD</span></div>
            </div>
          ))}
          {/* Formula note */}
          <div style={{ margin: '12px 16px', background: COLORS.gray100, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: FONTS.number, fontSize: 11, color: COLORS.gray600 }}>1.500 BHD × {selectedSession.students} students</div>
          </div>
        </div>
      </PhoneFrame>
    );
  }

  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Dark header */}
        <div style={{ background: COLORS.gray900, padding: '20px 16px 18px', textAlign: 'center' }}>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray400, marginBottom: 4 }}>This Month</div>
          <div style={{ fontFamily: FONTS.number, fontSize: 32, fontWeight: 800, color: COLORS.accent }}>520.000 <span style={{ fontSize: 14, fontWeight: 600 }}>BHD</span></div>
          <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500, marginTop: 2 }}>earned</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {[{ n: '18', l: 'Sessions' }, { n: '324', l: 'Students' }, { n: '4,280', l: 'YTD (BHD)' }].map((s) => (
              <div key={s.l} style={{ flex: 1, background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '8px 4px', textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 16, fontWeight: 700, color: COLORS.white }}>{s.n}</div>
                <div style={{ fontSize: 9, color: COLORS.gray500, fontFamily: FONTS.body }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Session Breakdown */}
        <SectionTitle>Session Breakdown</SectionTitle>
        {sessions.map((s, i) => (
          <div key={i} onClick={() => { setSelectedSession(s); setEarningsView('detail'); }}
            style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}`, cursor: 'pointer' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{s.name}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{s.date} &middot; {s.students} students</div>
            </div>
            <div style={{ fontFamily: FONTS.number, fontSize: 13, fontWeight: 700, color: COLORS.success }}>{s.earned} <span style={{ fontSize: 9, fontWeight: 500, color: COLORS.gray500 }}>BHD</span></div>
            <span style={{ color: COLORS.gray400, fontSize: 12, marginLeft: 6 }}>›</span>
          </div>
        ))}

        {/* Rate Formula */}
        <div style={{ margin: '12px 16px', background: COLORS.gray100, borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500, marginBottom: 4 }}>Rate Formula</div>
          <div style={{ fontFamily: FONTS.number, fontSize: 13, fontWeight: 700, color: COLORS.gray800 }}>1.500 BHD <span style={{ fontWeight: 400, color: COLORS.gray500 }}>x students / session</span></div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function CoachSchedule() {
  const days = ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th'];
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Weekly Schedule</div>
        </div>

        {/* Day Selector */}
        <div style={{ display: 'flex', padding: '10px 16px', gap: 6 }}>
          {days.map((d) => (
            <div key={d} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 10, background: d === 'Tu' ? COLORS.primary : COLORS.gray100, color: d === 'Tu' ? COLORS.white : COLORS.gray600, fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{d}</div>
          ))}
        </div>

        {/* Tuesday label */}
        <div style={{ padding: '8px 16px 4px', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, color: COLORS.gray800 }}>Tuesday, Feb 18</div>

        {/* Sessions */}
        {[
          { name: 'Kids Karate', time: '3:00 - 4:00 PM', room: 'Room A', students: 15, color: '#FED7E2' },
          { name: 'Adult Karate', time: '5:00 - 6:00 PM', room: 'Room A', students: 20, color: '#BEE3F8' },
          { name: 'Advanced Kata', time: '7:00 - 8:00 PM', room: 'Room B', students: 8, color: '#FEFCBF' },
        ].map((s) => (
          <div key={s.name} style={{ margin: '6px 16px', background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.gray200}`, overflow: 'hidden', display: 'flex' }}>
            <div style={{ width: 5, background: s.color, flexShrink: 0 }} />
            <div style={{ padding: '12px 14px', flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>{s.name}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500, marginTop: 2 }}>{s.time}</div>
              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                <span style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>📍 {s.room}</span>
                <span style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>👥 {s.students} students</span>
              </div>
            </div>
          </div>
        ))}

        {/* Other days preview */}
        <div style={{ padding: '14px 16px 4px', fontFamily: FONTS.heading, fontSize: 11, fontWeight: 700, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rest of the Week</div>
        {[
          { day: 'Wednesday', count: 2 },
          { day: 'Thursday', count: 1 },
          { day: 'Saturday', count: 3 },
          { day: 'Sunday', count: 2 },
        ].map((d) => (
          <div key={d.day} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <div style={{ flex: 1, fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray700 }}>{d.day}</div>
            <div style={{ fontFamily: FONTS.number, fontSize: 11, color: COLORS.gray500 }}>{d.count} sessions</div>
          </div>
        ))}
      </div>
    </PhoneFrame>
  );
}

function CoachLeave() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Availability &amp; Leave</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>Manage your leave requests</div>
        </div>

        {/* Mark Leave */}
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>Request Leave</div>
          <div>
            <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Date(s)</label>
            <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: COLORS.gray800 }}>Feb 25 - Feb 27, 2026</span>
              <span style={{ color: COLORS.gray400 }}>📅</span>
            </div>
          </div>
          <div>
            <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Reason</label>
            <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray400, minHeight: 50 }}>Personal / medical / travel...</div>
          </div>
          <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '12px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Submit Leave Request</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EBF8FF', borderRadius: 8, padding: '8px 10px' }}>
            <span style={{ fontSize: 12 }}>ℹ️</span>
            <span style={{ fontFamily: FONTS.body, fontSize: 10, color: '#2B6CB0' }}>Admin will be notified immediately</span>
          </div>
        </div>

        {/* Leave History */}
        <SectionTitle>Leave History</SectionTitle>
        {[
          { dates: 'Feb 5 - Feb 6', reason: 'Personal', status: 'Approved', statusColor: COLORS.success },
          { dates: 'Jan 20', reason: 'Medical', status: 'Approved', statusColor: COLORS.success },
          { dates: 'Jan 10 - Jan 12', reason: 'Travel', status: 'Approved', statusColor: COLORS.success },
          { dates: 'Dec 15', reason: 'Personal', status: 'Rejected', statusColor: COLORS.error },
        ].map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{l.dates}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{l.reason}</div>
            </div>
            <Badge label={l.status} bg={l.statusColor} />
          </div>
        ))}
      </div>
    </PhoneFrame>
  );
}

function CoachProfile() {
  const coachNav = [
    { icon: '🏠', label: 'Home' },
    { icon: '📅', label: 'Schedule' },
    { icon: '💰', label: 'Earnings' },
    { icon: '👤', label: 'Profile' },
  ];
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Hero */}
        <div style={{ background: `linear-gradient(135deg, ${COLORS.gray900}, #4A1942)`, padding: '24px 16px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: 36, background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: COLORS.white, fontFamily: FONTS.number, marginBottom: 10, border: `3px solid rgba(255,255,255,0.3)` }}>YS</div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 17, fontWeight: 700, color: COLORS.white, marginBottom: 2 }}>Yousif Salman</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray400, marginBottom: 12 }}>Head Coach &middot; Karate</div>
          <div style={{ display: 'flex', gap: 16 }}>
            {[{ v: '32', l: 'Students' }, { v: '18', l: 'Sessions/mo' }, { v: '12 yrs', l: 'Experience' }].map((s) => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 16, fontWeight: 800, color: COLORS.white }}>{s.v}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray400 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Info cards */}
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Personal Info */}
          <div style={{ background: COLORS.white, borderRadius: 12, padding: '12px 14px', border: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 700, color: COLORS.gray700, marginBottom: 10 }}>Personal Info</div>
            {[
              { label: 'Phone', value: '+973 3600 0000' },
              { label: 'Email', value: 'yousif@dojo.bh' },
              { label: 'Date of Birth', value: '15 Mar 1988' },
              { label: 'Nationality', value: 'Bahraini' },
            ].map((r) => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${COLORS.gray100}`, fontFamily: FONTS.body, fontSize: 11 }}>
                <span style={{ color: COLORS.gray500 }}>{r.label}</span>
                <span style={{ color: COLORS.gray800, fontWeight: 600 }}>{r.value}</span>
              </div>
            ))}
          </div>

          {/* Professional */}
          <div style={{ background: COLORS.white, borderRadius: 12, padding: '12px 14px', border: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 700, color: COLORS.gray700, marginBottom: 10 }}>Professional</div>
            {[
              { label: 'Discipline', value: 'Karate' },
              { label: 'Belt / Qualification', value: 'Black Belt 3rd Dan' },
              { label: 'Commission', value: '1.000 BHD / student' },
              { label: 'Status', value: 'Active' },
            ].map((r) => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${COLORS.gray100}`, fontFamily: FONTS.body, fontSize: 11 }}>
                <span style={{ color: COLORS.gray500 }}>{r.label}</span>
                <span style={{ color: COLORS.gray800, fontWeight: 600 }}>{r.value}</span>
              </div>
            ))}
          </div>

          {/* Earnings snapshot */}
          <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #9B1830)`, borderRadius: 12, padding: '12px 14px', color: COLORS.white }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>This Month</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: FONTS.number, fontSize: 22, fontWeight: 800 }}>520.000</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 9, opacity: 0.8 }}>BHD Earned</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 22, fontWeight: 800 }}>98%</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 9, opacity: 0.8 }}>Attendance</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <button style={{ background: COLORS.gray100, border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '11px 0', fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray700, cursor: 'pointer', width: '100%' }}>
            ✏️ Edit Profile
          </button>
          <button style={{ background: 'transparent', border: `1px solid ${COLORS.error}`, borderRadius: 10, padding: '11px 0', fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.error, cursor: 'pointer', width: '100%' }}>
            Log Out
          </button>
        </div>
      </div>
      <BottomNav items={coachNav} active="Profile" />
    </PhoneFrame>
  );
}

function CoachPayments() {
  const paymentData = [
    { name: 'Fahad Al-Ahmed', plan: 'Monthly Kids', amount: '45.000', status: 'Paid', statusBg: '#C6F6D5', statusColor: '#38A169' },
    { name: 'Sara Ahmed', plan: 'Monthly Adult', amount: '55.000', status: 'Overdue', statusBg: '#FED7D7', statusColor: '#C53030' },
    { name: 'Omar Hassan', plan: 'Quarterly', amount: '120.000', status: 'Paid', statusBg: '#C6F6D5', statusColor: '#38A169' },
    { name: 'Layla Khalifa', plan: 'Monthly Kids', amount: '45.000', status: 'Due Soon', statusBg: '#FEFCBF', statusColor: '#D69E2E' },
    { name: 'Ali Mansoor', plan: 'Monthly Adult', amount: '55.000', status: 'Overdue', statusBg: '#FED7D7', statusColor: '#C53030' },
    { name: 'Noor Khalid', plan: 'Monthly Kids', amount: '45.000', status: 'Due Soon', statusBg: '#FEFCBF', statusColor: '#D69E2E' },
    { name: 'Hassan Ibrahim', plan: 'Monthly Adult', amount: '55.000', status: 'Paid', statusBg: '#C6F6D5', statusColor: '#38A169' },
    { name: 'Khalid Tariq', plan: 'Quarterly', amount: '120.000', status: 'Upcoming', statusBg: COLORS.gray200, statusColor: COLORS.gray600 },
  ];

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Phone 1 — Student Payments List */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Student Payments</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>Your students</div>
          </div>

          {/* Filter Pills */}
          <div style={{ display: 'flex', gap: 8, padding: '10px 16px' }}>
            {['All', 'Overdue'].map((tab, i) => (
              <div key={tab} style={{ padding: '6px 16px', borderRadius: 20, fontSize: 11, fontFamily: FONTS.body, fontWeight: 600, cursor: 'pointer', background: i === 0 ? COLORS.primary : COLORS.gray200, color: i === 0 ? COLORS.white : COLORS.gray600 }}>{tab}</div>
            ))}
          </div>

          {/* Student List */}
          {paymentData.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
              <div style={{ width: 32, height: 32, borderRadius: 16, background: COLORS.gray200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: COLORS.gray700, marginRight: 10 }}>{s.name[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{s.name}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{s.plan}</div>
              </div>
              <div style={{ fontFamily: FONTS.number, fontSize: 11, fontWeight: 700, color: COLORS.gray800, marginRight: 8 }}>{s.amount}</div>
              <div style={{ background: s.statusBg, color: s.statusColor, borderRadius: 8, padding: '3px 8px', fontSize: 9, fontFamily: FONTS.body, fontWeight: 600, marginRight: 6 }}>{s.status}</div>
              <div style={{ width: 26, height: 26, borderRadius: 13, background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, cursor: 'pointer' }}>💬</div>
            </div>
          ))}
        </div>
      </PhoneFrame>

      {/* Phone 2 — WhatsApp Compose */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Payment Reminder</div>
          </div>
          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* To Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray600 }}>To:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${COLORS.gray200}`, borderRadius: 20, padding: '4px 10px', background: COLORS.gray100 }}>
                <div style={{ width: 20, height: 20, borderRadius: 10, background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: COLORS.white, fontWeight: 700 }}>S</div>
                <span style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray800, fontWeight: 600 }}>Sara Ahmed</span>
              </div>
            </div>

            {/* Message Template */}
            <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 12, padding: '14px', background: COLORS.gray100, minHeight: 140 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray800, lineHeight: 1.6 }}>
                Dear Sara,<br /><br />
                Your monthly fee of 45 BHD is overdue.<br />
                Please pay via Benefit Pay.<br /><br />
                DojoMaster Team 🥋
              </div>
            </div>

            {/* Edit Link */}
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.primary, cursor: 'pointer', fontWeight: 600 }}>Edit message</span>
            </div>

            {/* Send Button */}
            <div style={{ background: '#25D366', color: COLORS.white, borderRadius: 12, padding: '13px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Send via WhatsApp 💬</div>

            {/* Info Note */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ fontSize: 12 }}>ℹ️</span>
              <span style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>WhatsApp will open with this template pre-filled</span>
            </div>
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}

function StudentLanding() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Hero */}
        <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, padding: '28px 16px 24px', textAlign: 'center', color: COLORS.white }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 24, fontWeight: 800, letterSpacing: -0.5 }}>DojoMaster</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, opacity: 0.85, marginTop: 4 }}>Train. Track. Transform.</div>
          <div style={{ width: 50, height: 50, borderRadius: 25, background: 'rgba(255,255,255,0.15)', margin: '16px auto 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🥋</div>
        </div>

        {/* Disciplines carousel */}
        <SectionTitle>Disciplines</SectionTitle>
        <div style={{ display: 'flex', gap: 10, padding: '4px 16px 8px', overflowX: 'auto' }}>
          {[
            { name: 'Karate', emoji: '🥋' },
            { name: 'Boxing', emoji: '🥊' },
            { name: 'BJJ', emoji: '🤼' },
            { name: 'Kickboxing', emoji: '🦵' },
            { name: 'Muay Thai', emoji: '🇹🇭' },
            { name: 'Krav Maga', emoji: '🛡️' },
          ].map((d) => (
            <div key={d.name} style={{ minWidth: 70, textAlign: 'center', background: COLORS.gray100, borderRadius: 12, padding: '12px 8px', flexShrink: 0 }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{d.emoji}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray700 }}>{d.name}</div>
            </div>
          ))}
        </div>

        {/* Plans */}
        <SectionTitle>Membership Plans</SectionTitle>
        {[
          { name: 'Monthly Kids', price: '45', features: '4x/week, Gi included' },
          { name: 'Monthly Adult', price: '55', features: '4x/week, Open gym' },
          { name: 'Quarterly', price: '120', features: 'Save 20%, All access' },
        ].map((p) => (
          <div key={p.name} style={{ margin: '6px 16px', background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.gray200}`, padding: '12px 14px', display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>{p.name}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{p.features}</div>
            </div>
            <div style={{ fontFamily: FONTS.number, fontSize: 18, fontWeight: 700, color: COLORS.primary }}>{p.price} <span style={{ fontSize: 10, fontWeight: 500, color: COLORS.gray500 }}>BHD</span></div>
          </div>
        ))}

        {/* CTA */}
        <div style={{ padding: '16px' }}>
          <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '14px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Join Now</div>
          <div style={{ textAlign: 'center', marginTop: 10, fontFamily: FONTS.body, fontSize: 12, color: COLORS.primary, cursor: 'pointer', fontWeight: 600 }}>Already a member? Log in</div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function StudentLogin() {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Role Selection */}
      <PhoneFrame>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 16px 20px' }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 20, fontWeight: 800, color: COLORS.primary, marginBottom: 4 }}>DojoMaster</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500, marginBottom: 28 }}>Select your role</div>
          {[
            { role: 'Coach', icon: '🥋', desc: 'Train and track students', active: false },
            { role: 'Student', icon: '⭐', desc: 'Track your journey', active: true },
          ].map((r) => (
            <div key={r.role} style={{ width: '100%', border: `2px solid ${r.active ? COLORS.primary : COLORS.gray200}`, borderRadius: 14, padding: '16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: r.active ? '#FFF5F5' : COLORS.white }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: r.active ? COLORS.primary : COLORS.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: r.active ? COLORS.white : undefined }}>
                {r.icon}
              </div>
              <div>
                <div style={{ fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>{r.role}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{r.desc}</div>
              </div>
              {r.active && <div style={{ marginLeft: 'auto', color: COLORS.primary, fontSize: 16 }}>●</div>}
            </div>
          ))}
          <div style={{ marginTop: 8, background: COLORS.gray100, borderRadius: 10, padding: '10px 12px', width: '100%', boxSizing: 'border-box' }}>
            <span style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray600 }}>If admin registered you, log in with your mobile number</span>
          </div>
        </div>
      </PhoneFrame>

      {/* Login Form — Student selected (OTP flow) */}
      <PhoneFrame>
        <div style={{ flex: 1, padding: '30px 16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 20, fontWeight: 800, color: COLORS.gray800, marginBottom: 4 }}>Welcome Back</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500, marginBottom: 28 }}>Sign in as Student</div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontFamily: FONTS.body, fontSize: 11, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 6 }}>Mobile Number</label>
            <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '12px 14px', fontSize: 13, fontFamily: FONTS.body, color: COLORS.gray400 }}>+973 XXXX XXXX</div>
          </div>

          <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '14px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Send OTP</div>

          <div style={{ marginTop: 16, textAlign: 'center', fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500 }}>
            New student? <span style={{ color: COLORS.primary, fontWeight: 600, cursor: 'pointer' }}>Sign up</span>
          </div>

          <div style={{ marginTop: 'auto', background: COLORS.gray100, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
            <span style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Coaches log in with their ID + Password</span>
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}

function StudentSignup() {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Phone 1 — Step 1: Your Details */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, padding: '20px 16px 16px', color: COLORS.white }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 18, fontWeight: 800 }}>Join DojoMaster</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, opacity: 0.8, marginTop: 2 }}>Step 1 of 2</div>
          </div>
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Full Name', placeholder: 'Enter your full name' },
              { label: 'Mobile Number', placeholder: '+973 XXXX XXXX' },
              { label: 'Date of Birth', placeholder: 'DD / MM / YYYY' },
            ].map((f) => (
              <div key={f.label}>
                <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>{f.label}</label>
                <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '11px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray400 }}>{f.placeholder}</div>
              </div>
            ))}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Gender</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '11px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray800, display: 'flex', justifyContent: 'space-between' }}>
                <span>Select gender</span><span style={{ color: COLORS.gray400 }}>▾</span>
              </div>
            </div>
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Discipline</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '11px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray800, display: 'flex', justifyContent: 'space-between' }}>
                <span>Select discipline</span><span style={{ color: COLORS.gray400 }}>▾</span>
              </div>
            </div>

            {/* Plan Selection */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 8 }}>Select Plan</label>
              {[
                { name: 'Monthly Kids', price: '45 BHD', selected: true },
                { name: 'Monthly Adult', price: '55 BHD', selected: false },
                { name: 'Quarterly', price: '120 BHD', selected: false },
              ].map((plan) => (
                <div key={plan.name} style={{ border: `2px solid ${plan.selected ? COLORS.primary : COLORS.gray200}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', cursor: 'pointer', background: plan.selected ? '#FFF5F5' : COLORS.white }}>
                  <div style={{ width: 16, height: 16, borderRadius: 8, border: `2px solid ${plan.selected ? COLORS.primary : COLORS.gray300}`, marginRight: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {plan.selected && <div style={{ width: 8, height: 8, borderRadius: 4, background: COLORS.primary }} />}
                  </div>
                  <div style={{ flex: 1, fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{plan.name}</div>
                  <div style={{ fontFamily: FONTS.number, fontSize: 13, fontWeight: 700, color: plan.selected ? COLORS.primary : COLORS.gray600 }}>{plan.price}</div>
                </div>
              ))}
            </div>

            <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '13px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Continue →</div>
          </div>
        </div>
      </PhoneFrame>

      {/* Phone 2 — Step 2: Payment */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, padding: '20px 16px 16px', color: COLORS.white }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 800 }}>Step 2 of 2 — Payment</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, opacity: 0.8, marginTop: 2 }}>Almost there!</div>
          </div>
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Order Summary */}
            <div style={{ background: COLORS.gray100, borderRadius: 12, padding: '14px' }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Order Summary</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>Monthly Kids Plan</div>
                <div style={{ fontFamily: FONTS.number, fontSize: 15, fontWeight: 700, color: COLORS.primary }}>45.000 BHD</div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 8 }}>Payment Method</label>
              <div style={{ border: `2px solid ${COLORS.primary}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, background: '#FFF5F5', cursor: 'pointer' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#0072BC', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontWeight: 700, fontSize: 11, fontFamily: FONTS.number }}>BP</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>Benefit Pay</div>
                  <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Instant payment</div>
                </div>
                <div style={{ width: 16, height: 16, borderRadius: 8, border: `2px solid ${COLORS.primary}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: COLORS.primary }} />
                </div>
              </div>
            </div>

            {/* Mobile Field */}
            <div>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>Your Benefit Pay number</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '11px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray800 }}>+973 XXXX XXXX</div>
            </div>

            {/* Info Note */}
            <div style={{ background: '#EBF8FF', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ fontSize: 12 }}>ℹ️</span>
              <span style={{ fontFamily: FONTS.body, fontSize: 10, color: '#2B6CB0' }}>You'll receive a Benefit Pay payment request. Complete to activate membership.</span>
            </div>

            <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '13px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Confirm &amp; Join</div>
            <div style={{ textAlign: 'center', fontFamily: FONTS.body, fontSize: 12, color: COLORS.primary, cursor: 'pointer', fontWeight: 600 }}>← Back</div>
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}

function StudentOTP() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px 24px' }}>
        {/* Icon */}
        <div style={{ width: 64, height: 64, borderRadius: 32, background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 20 }}>🔐</div>

        {/* Heading */}
        <div style={{ fontFamily: FONTS.heading, fontSize: 20, fontWeight: 700, color: COLORS.gray800, textAlign: 'center', marginBottom: 8 }}>Verify Your Number</div>

        {/* Sub-text */}
        <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500, textAlign: 'center', marginBottom: 28 }}>
          Code sent to +973 XXXX XXXX &middot; <span style={{ color: COLORS.primary, fontWeight: 600, cursor: 'pointer' }}>Change number?</span>
        </div>

        {/* OTP Boxes */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {['•', '•', '•', '•', '', ''].map((val, i) => (
            <div key={i} style={{
              width: 44, height: 52, borderRadius: 10,
              border: `2px solid ${val ? COLORS.primary : COLORS.gray300}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontFamily: FONTS.number, fontWeight: 700, color: COLORS.gray800,
              background: val ? '#FFF5F5' : COLORS.white,
            }}>{val}</div>
          ))}
        </div>

        {/* Verify Button */}
        <div style={{ width: '100%', background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '14px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>Verify</div>

        {/* Resend Row */}
        <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500, textAlign: 'center' }}>
          Didn't receive? <span style={{ color: COLORS.primary, fontWeight: 600, cursor: 'pointer' }}>Resend</span> <span style={{ color: COLORS.gray400 }}>30s</span>
        </div>
      </div>
    </PhoneFrame>
  );
}

function StudentDashboard() {
  const studentNav = [
    { icon: '🏠', label: 'Home' },
    { icon: '📷', label: 'Scan' },
    { icon: '📈', label: 'Progress' },
    { icon: '💳', label: 'Pay' },
    { icon: '👤', label: 'Me' },
  ];
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, padding: '16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 24, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, border: '2px solid rgba(255,255,255,0.4)' }}>F</div>
          <div style={{ color: COLORS.white }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700 }}>Fahad</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 10, opacity: 0.85 }}>Green Belt &middot; Coach Ahmed</div>
          </div>
        </div>

        {/* Belt Progress */}
        <div style={{ margin: '12px 16px', background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.gray200}`, padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>Belt Progress</span>
            <span style={{ fontFamily: FONTS.number, fontSize: 11, fontWeight: 700, color: COLORS.primary }}>93%</span>
          </div>
          <div style={{ height: 8, background: COLORS.gray200, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: '93%', height: '100%', background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent})`, borderRadius: 4 }} />
          </div>
          <div style={{ fontFamily: FONTS.number, fontSize: 10, color: COLORS.gray500, marginTop: 4 }}>186 / 200 hrs to Blue Belt</div>
        </div>

        {/* Scan QR Card */}
        <div style={{ margin: '0 16px 12px', background: COLORS.gray900, borderRadius: 12, padding: '14px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: COLORS.white }}>📷</div>
          <div>
            <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.white }}>Scan QR to Check In</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray400 }}>Scan QR at reception desk</div>
          </div>
        </div>

        {/* Next Session */}
        <div style={{ margin: '0 16px 12px', background: '#FFF5F5', borderRadius: 12, border: `1px solid #FED7D7`, padding: '12px 14px' }}>
          <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.primary, fontWeight: 600, marginBottom: 4 }}>NEXT SESSION</div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>Adult Karate</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>Today, 5:00 - 6:00 PM &middot; Room A</div>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 16px', marginBottom: 12 }}>
          <div style={{ background: COLORS.white, borderRadius: 10, border: `1px solid ${COLORS.gray200}`, padding: '10px', textAlign: 'center' }}>
            <div style={{ fontFamily: FONTS.number, fontSize: 20, fontWeight: 700, color: COLORS.success }}>92%</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray500 }}>Attendance</div>
          </div>
          <div style={{ background: COLORS.white, borderRadius: 10, border: `1px solid ${COLORS.gray200}`, padding: '10px', textAlign: 'center' }}>
            <div style={{ fontFamily: FONTS.number, fontSize: 20, fontWeight: 700, color: COLORS.accent }}>4</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray500 }}>Badges</div>
          </div>
        </div>

        {/* Homework */}
        <SectionTitle>Homework (2 Pending)</SectionTitle>
        {[
          { name: 'Kata Heian Shodan', due: 'Due Feb 22' },
          { name: 'Roundhouse Combo', due: 'Due Feb 24' },
        ].map((h) => (
          <div key={h.name} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: COLORS.accent, marginRight: 10 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{h.name}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{h.due}</div>
            </div>
            <span style={{ fontSize: 12, color: COLORS.gray400 }}>›</span>
          </div>
        ))}
        <div style={{ height: 8 }} />
      </div>
      <BottomNav items={studentNav} active="Home" />
    </PhoneFrame>
  );
}

function ParentView() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ background: COLORS.gray900, padding: '18px 16px', color: COLORS.white }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700 }}>Mohammed Al-Dosari</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, opacity: 0.7 }}>Parent &middot; 2 children</div>
        </div>

        {/* Child Cards */}
        <SectionTitle>My Children</SectionTitle>
        {/* Fahad */}
        <div style={{ margin: '6px 16px', background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.gray200}`, overflow: 'hidden' }}>
          <div style={{ padding: '14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 20, background: '#C6F6D5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: COLORS.success }}>F</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>Fahad</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Green Belt &middot; Karate</div>
            </div>
            <Badge label="Green" bg="#C6F6D5" color={COLORS.success} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderTop: `1px solid ${COLORS.gray100}` }}>
            {[
              { v: '92%', l: 'Attend.' },
              { v: '186', l: 'Hours' },
              { v: '2', l: 'Homework' },
              { v: '4', l: 'Badges' },
            ].map((s) => (
              <div key={s.l} style={{ textAlign: 'center', padding: '10px 4px', borderRight: `1px solid ${COLORS.gray100}` }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>{s.v}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 8, color: COLORS.gray500 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Noor */}
        <div style={{ margin: '6px 16px', background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.gray200}`, overflow: 'hidden' }}>
          <div style={{ padding: '14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 20, background: '#FEFCBF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#D69E2E' }}>N</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>Noor</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Yellow Belt &middot; Karate</div>
            </div>
            <Badge label="Yellow" bg="#FEFCBF" color="#D69E2E" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderTop: `1px solid ${COLORS.gray100}` }}>
            {[
              { v: '78%', l: 'Attend.' },
              { v: '95', l: 'Hours' },
              { v: '1', l: 'Homework' },
              { v: '2', l: 'Badges' },
            ].map((s) => (
              <div key={s.l} style={{ textAlign: 'center', padding: '10px 4px', borderRight: `1px solid ${COLORS.gray100}` }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>{s.v}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 8, color: COLORS.gray500 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Payments */}
        <SectionTitle>Payments</SectionTitle>
        <div style={{ margin: '4px 16px', background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.gray200}`, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>Fahad - Monthly Kids</div>
              <div style={{ fontFamily: FONTS.number, fontSize: 11, color: COLORS.gray500 }}>45.000 BHD</div>
            </div>
            <Badge label="Paid" bg="#C6F6D5" color={COLORS.success} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>Noor - Monthly Kids</div>
              <div style={{ fontFamily: FONTS.number, fontSize: 11, color: COLORS.gray500 }}>45.000 BHD</div>
            </div>
            <Badge label="Overdue" bg="#FED7D7" color={COLORS.error} />
          </div>
        </div>

        <div style={{ padding: '12px 16px' }}>
          <div style={{ background: '#2563EB', color: COLORS.white, borderRadius: 12, padding: '13px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Pay via Benefit Pay</div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function StudentQR() {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Scanner Phone */}
      <PhoneFrame>
        <div style={{ flex: 1, background: COLORS.gray900, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.white, marginBottom: 20 }}>Scan QR Code</div>
          {/* Camera Viewfinder */}
          <div style={{ width: 200, height: 200, position: 'relative', marginBottom: 20 }}>
            {/* Corner guides */}
            {[
              { top: 0, left: 0, borderTop: `3px solid ${COLORS.accent}`, borderLeft: `3px solid ${COLORS.accent}` },
              { top: 0, right: 0, borderTop: `3px solid ${COLORS.accent}`, borderRight: `3px solid ${COLORS.accent}` },
              { bottom: 0, left: 0, borderBottom: `3px solid ${COLORS.accent}`, borderLeft: `3px solid ${COLORS.accent}` },
              { bottom: 0, right: 0, borderBottom: `3px solid ${COLORS.accent}`, borderRight: `3px solid ${COLORS.accent}` },
            ].map((s, i) => (
              <div key={i} style={{ position: 'absolute', width: 30, height: 30, ...s }} />
            ))}
            {/* Viewfinder area */}
            <div style={{ position: 'absolute', inset: 10, border: '1px solid rgba(255,215,0,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 40, height: 40, border: '2px solid rgba(255,215,0,0.5)', borderRadius: 4 }} />
            </div>
            {/* Scan line animation placeholder */}
            <div style={{ position: 'absolute', top: '45%', left: 10, right: 10, height: 2, background: `linear-gradient(90deg, transparent, ${COLORS.accent}, transparent)` }} />
          </div>
          <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray400, textAlign: 'center' }}>Scan QR at reception desk</div>
        </div>
      </PhoneFrame>

      {/* Check-In Success Phone */}
      <PhoneFrame>
        <div style={{ flex: 1, background: `linear-gradient(180deg, ${COLORS.success}, #276749)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px', color: COLORS.white }}>
          {/* Checkmark Circle */}
          <div style={{ width: 80, height: 80, borderRadius: 40, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: 28, background: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>✓</div>
          </div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Checked In!</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 12, opacity: 0.85, marginBottom: 24 }}>Adult Karate &middot; 5:00-6:00 PM</div>

          <div style={{ display: 'flex', gap: 24, marginBottom: 8 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800 }}>12</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, opacity: 0.8 }}>Day Streak</div>
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.3)' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800 }}>187</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, opacity: 0.8 }}>Total Hours</div>
            </div>
          </div>
        </div>
      </PhoneFrame>

      {/* Phone 3 — Error States */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto', background: COLORS.gray100 }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}`, background: COLORS.white }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>QR Error States</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>All possible scan failures</div>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { icon: '⏳', title: 'Session Not Started', desc: "Coach hasn't started the session yet. Check your schedule and try again when class begins.", color: '#744210', bg: '#FEFCBF', border: '#F6E05E' },
              { icon: '🔒', title: 'Session Ended', desc: 'This session has already ended. Contact admin if you believe this is an error.', color: '#742A2A', bg: '#FFF5F5', border: '#FC8181' },
              { icon: '❌', title: 'Not Enrolled', desc: "You're not enrolled in this class. Please register with admin or check your membership plan.", color: '#44337A', bg: '#FAF5FF', border: '#B794F4' },
              { icon: '🔁', title: 'QR Code Expired', desc: 'This QR code has expired. Ask your coach to refresh it and try scanning again.', color: '#2A4365', bg: '#EBF8FF', border: '#63B3ED' },
            ].map((e) => (
              <div key={e.title} style={{ background: e.bg, border: `1px solid ${e.border}`, borderRadius: 12, padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: e.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{e.icon}</div>
                  <div style={{ fontFamily: FONTS.heading, fontSize: 12, fontWeight: 700, color: e.color }}>{e.title}</div>
                </div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: e.color, lineHeight: 1.5, opacity: 0.85 }}>{e.desc}</div>
                <button style={{ marginTop: 10, background: e.border, border: 'none', borderRadius: 8, padding: '6px 14px', fontFamily: FONTS.body, fontSize: 10, fontWeight: 700, color: e.color, cursor: 'pointer' }}>
                  Try Again
                </button>
              </div>
            ))}
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}

function StudentProgress() {
  const studentNav = [
    { icon: '🏠', label: 'Home' },
    { icon: '📷', label: 'Scan' },
    { icon: '📈', label: 'Progress' },
    { icon: '💳', label: 'Pay' },
    { icon: '👤', label: 'Me' },
  ];
  const belts = [
    { name: 'White', color: '#FFFFFF', border: '#E2E8F0', done: true },
    { name: 'Yellow', color: '#F6E05E', border: '#F6E05E', done: true },
    { name: 'Orange', color: '#ED8936', border: '#ED8936', done: true },
    { name: 'Green', color: '#48BB78', border: '#48BB78', done: true },
    { name: 'Blue', color: '#4299E1', border: '#4299E1', done: false },
    { name: 'Brown', color: '#A0522D', border: '#A0522D', done: false },
    { name: 'Black', color: '#1A202C', border: '#1A202C', done: false },
  ];

  // Feb calendar
  const febDays = Array.from({ length: 28 }, (_, i) => {
    const day = i + 1;
    if (day > 18) return { day, status: 'future' };
    const rng = Math.sin(day * 7) * 0.5 + 0.5;
    return { day, status: rng > 0.2 ? 'present' : 'absent' };
  });

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>My Progress</div>
        </div>

        {/* Current Belt Progress */}
        <div style={{ margin: '12px 16px', background: '#F0FFF4', borderRadius: 12, border: `1px solid #C6F6D5`, padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <span style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, color: '#276749' }}>Green Belt</span>
              <span style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}> to Blue</span>
            </div>
            <span style={{ fontFamily: FONTS.number, fontSize: 12, fontWeight: 700, color: COLORS.primary }}>186/200 hrs</span>
          </div>
          <div style={{ height: 10, background: '#C6F6D5', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ width: '93%', height: '100%', background: '#48BB78', borderRadius: 5 }} />
          </div>
        </div>

        {/* Belt Journey */}
        <SectionTitle>Belt Journey</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', gap: 4 }}>
          {belts.map((b, i) => (
            <React.Fragment key={b.name}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: b.color, border: `2px solid ${b.border}`, opacity: b.done ? 1 : 0.35, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {b.done && <span style={{ fontSize: 10, color: b.name === 'White' ? COLORS.gray500 : COLORS.white }}>✓</span>}
                </div>
                <span style={{ fontFamily: FONTS.body, fontSize: 7, color: b.done ? COLORS.gray700 : COLORS.gray400 }}>{b.name}</span>
              </div>
              {i < belts.length - 1 && (
                <div style={{ flex: 1, height: 2, background: b.done && belts[i + 1]?.done ? COLORS.success : COLORS.gray200, minWidth: 6 }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Achievement Badges */}
        <SectionTitle>Achievements</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, padding: '4px 16px 8px' }}>
          {[
            { icon: '🔥', name: '10-Day Streak', earned: true },
            { icon: '⏰', name: '100 Hours', earned: true },
            { icon: '⭐', name: 'Perfect Week', earned: true },
            { icon: '🥋', name: 'Belt Up!', earned: true },
          ].map((a) => (
            <div key={a.name} style={{ textAlign: 'center', background: a.earned ? '#FFFBEB' : COLORS.gray100, borderRadius: 10, padding: '10px 4px', border: a.earned ? `1px solid ${COLORS.accent}` : `1px solid ${COLORS.gray200}` }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{a.icon}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 8, fontWeight: 600, color: a.earned ? COLORS.gray800 : COLORS.gray400 }}>{a.name}</div>
            </div>
          ))}
        </div>

        {/* Feb Attendance Calendar */}
        <SectionTitle>February Attendance</SectionTitle>
        <div style={{ padding: '4px 16px 12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontFamily: FONTS.body, fontSize: 8, color: COLORS.gray400, paddingBottom: 4 }}>{d}</div>
            ))}
            {/* Feb 2026 starts on Sunday (0 offset) */}
            {febDays.map((d) => (
              <div key={d.day} style={{ textAlign: 'center', padding: '4px 0' }}>
                <div style={{ width: 20, height: 20, borderRadius: 10, margin: '0 auto', background: d.status === 'present' ? '#C6F6D5' : d.status === 'absent' ? '#FED7D7' : COLORS.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: FONTS.number, fontSize: 8, color: d.status === 'present' ? COLORS.success : d.status === 'absent' ? COLORS.error : COLORS.gray400 }}>{d.day}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <BottomNav items={studentNav} active="Progress" />
    </PhoneFrame>

      {/* Phone 2 — Achievement Badges */}
      <PhoneFrame>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Achievements</div>
            <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>Badges earned through consistency &amp; progress</div>
          </div>

          {/* Stats strip */}
          <div style={{ display: 'flex', justifyContent: 'space-around', padding: '10px 16px', background: '#FFF5F5', borderBottom: `1px solid ${COLORS.gray200}` }}>
            {[{ v: '7', l: 'Earned' }, { v: '5', l: 'Locked' }, { v: '12', l: 'Day Streak' }].map((s) => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 20, fontWeight: 800, color: COLORS.primary }}>{s.v}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 9, color: COLORS.gray500 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Earned badges */}
          <div style={{ padding: '10px 16px 4px' }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 11, fontWeight: 700, color: COLORS.gray600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: .5 }}>Earned</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                { icon: '🥋', name: 'First Session', desc: 'Attended your first class', color: '#C41E3A', bg: '#FFF5F5' },
                { icon: '🔥', name: 'On Fire', desc: '7-day streak', color: '#DD6B20', bg: '#FFFAF0' },
                { icon: '⭐', name: 'Perfect Week', desc: 'Attended all sessions', color: '#ECC94B', bg: '#FFFFF0' },
                { icon: '💪', name: 'Iron Will', desc: '30 sessions total', color: '#3182CE', bg: '#EBF8FF' },
                { icon: '🏅', name: 'Belt Up', desc: 'Passed belt exam', color: '#805AD5', bg: '#FAF5FF' },
                { icon: '📅', name: '1 Month In', desc: 'Active for 30 days', color: '#38A169', bg: '#F0FFF4' },
                { icon: '⚡', name: 'Early Bird', desc: '5 morning sessions', color: '#DD6B20', bg: '#FFFAF0' },
              ].map((b) => (
                <div key={b.name} style={{ background: b.bg, border: `1px solid ${b.color}22`, borderRadius: 12, padding: '10px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 20, background: b.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, marginBottom: 4, boxShadow: `0 2px 8px ${b.color}44` }}>{b.icon}</div>
                  <div style={{ fontFamily: FONTS.heading, fontSize: 9, fontWeight: 700, color: COLORS.gray800, marginBottom: 2 }}>{b.name}</div>
                  <div style={{ fontFamily: FONTS.body, fontSize: 8, color: COLORS.gray500, lineHeight: 1.3 }}>{b.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Locked badges */}
          <div style={{ padding: '8px 16px 16px' }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: 11, fontWeight: 700, color: COLORS.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: .5 }}>Locked</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                { icon: '🥇', name: '100 Sessions', desc: '73 more to go' },
                { icon: '🏆', name: 'Champion', desc: 'Win a tournament' },
                { icon: '🌟', name: 'Legend', desc: 'Reach Black Belt' },
                { icon: '📚', name: 'Scholar', desc: 'Complete all exercises' },
                { icon: '🤝', name: 'Mentor', desc: 'Refer a student' },
              ].map((b) => (
                <div key={b.name} style={{ background: COLORS.gray100, border: `1px solid ${COLORS.gray200}`, borderRadius: 12, padding: '10px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', opacity: 0.6 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 20, background: COLORS.gray300, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, marginBottom: 4, filter: 'grayscale(1)' }}>🔒</div>
                  <div style={{ fontFamily: FONTS.heading, fontSize: 9, fontWeight: 700, color: COLORS.gray600, marginBottom: 2 }}>{b.name}</div>
                  <div style={{ fontFamily: FONTS.body, fontSize: 8, color: COLORS.gray400, lineHeight: 1.3 }}>{b.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <BottomNav items={studentNav} active="Progress" />
      </PhoneFrame>
    </div>
  );
}

function StudentExercises() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>My Exercises</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray500 }}>2 pending, 1 completed</div>
        </div>

        {/* Completed Exercise */}
        <div style={{ margin: '10px 16px', background: '#F0FFF4', borderRadius: 12, border: `1px solid #C6F6D5`, padding: '14px', opacity: 0.8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 28, height: 28, borderRadius: 14, background: COLORS.success, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontSize: 12 }}>✓</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800, textDecoration: 'line-through' }}>Front Kick Drill</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Completed Feb 17</div>
            </div>
            <Badge label="Done" bg={COLORS.success} />
          </div>
        </div>

        {/* Pending: Kata Heian Shodan */}
        <div style={{ margin: '10px 16px', background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.gray200}`, overflow: 'hidden' }}>
          <div style={{ padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 14, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>📝</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>Kata Heian Shodan</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Due Feb 22</div>
              </div>
              <Badge label="Pending" bg={COLORS.accent} color={COLORS.gray800} />
            </div>
            {/* YouTube Preview */}
            <div style={{ background: COLORS.gray900, borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.6))' }} />
                <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(255,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, fontSize: 14, zIndex: 1 }}>▶</div>
              </div>
              <div style={{ padding: '6px 10px', fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray400 }}>Heian Shodan Full Tutorial</div>
            </div>
            <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 10, padding: '10px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Mark Complete</div>
          </div>
        </div>

        {/* Pending: Roundhouse Combo */}
        <div style={{ margin: '10px 16px', background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.gray200}`, padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 14, background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>📝</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>Roundhouse Combo</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>Due Feb 24</div>
            </div>
            <Badge label="Pending" bg={COLORS.accent} color={COLORS.gray800} />
          </div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.gray600, marginBottom: 10 }}>Practice the 3-kick roundhouse combination from class. Focus on hip rotation and chamber position.</div>
          <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 10, padding: '10px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Mark Complete</div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function StudentPayments() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.gray200}` }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.gray800 }}>Payments</div>
        </div>

        {/* Current Plan Card */}
        <div style={{ margin: '12px 16px', background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, borderRadius: 14, padding: '18px 16px', color: COLORS.white }}>
          <div style={{ fontFamily: FONTS.body, fontSize: 10, opacity: 0.8, marginBottom: 2 }}>Current Plan</div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700, marginBottom: 2 }}>Monthly Kids</div>
          <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: COLORS.accent }}>45 <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>BHD</span></div>
          <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: 9, opacity: 0.7 }}>Frequency</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 11, fontWeight: 600 }}>4x / week</div>
            </div>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: 9, opacity: 0.7 }}>Renews</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 11, fontWeight: 600 }}>Mar 1</div>
            </div>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: 9, opacity: 0.7 }}>Sessions</div>
              <div style={{ fontFamily: FONTS.number, fontSize: 11, fontWeight: 600 }}>23</div>
            </div>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: 9, opacity: 0.7 }}>Days Left</div>
              <div style={{ fontFamily: FONTS.number, fontSize: 11, fontWeight: 600 }}>11</div>
            </div>
          </div>
        </div>

        {/* Pay Button */}
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ background: '#2563EB', color: COLORS.white, borderRadius: 12, padding: '13px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Pay with Benefit Pay</div>
        </div>

        {/* Payment History */}
        <SectionTitle>Payment History</SectionTitle>
        {[
          { month: 'February 2026', amount: '45.000', status: 'Paid', date: 'Feb 1' },
          { month: 'January 2026', amount: '45.000', status: 'Paid', date: 'Jan 1' },
          { month: 'December 2025', amount: '45.000', status: 'Paid', date: 'Dec 1' },
        ].map((p) => (
          <div key={p.month} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#C6F6D5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, fontSize: 14 }}>✓</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: COLORS.gray800 }}>{p.month}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{p.date}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: FONTS.number, fontSize: 13, fontWeight: 700, color: COLORS.gray800 }}>{p.amount} <span style={{ fontSize: 9, color: COLORS.gray500 }}>BHD</span></div>
              <Badge label={p.status} bg="#C6F6D5" color={COLORS.success} />
            </div>
          </div>
        ))}
      </div>
    </PhoneFrame>
  );
}

function StudentProfile() {
  const studentNav = [
    { icon: '🏠', label: 'Home' },
    { icon: '📷', label: 'Scan' },
    { icon: '📈', label: 'Progress' },
    { icon: '💳', label: 'Pay' },
    { icon: '👤', label: 'Me' },
  ];
  return (
    <PhoneFrame>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Profile Header */}
        <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #8B0000)`, padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', color: COLORS.white }}>
          <div style={{ width: 64, height: 64, borderRadius: 32, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, border: '3px solid rgba(255,255,255,0.4)', marginBottom: 10 }}>F</div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700 }}>Fahad Al-Ahmed</div>
          <div style={{ fontFamily: FONTS.body, fontSize: 11, opacity: 0.85, marginTop: 2 }}>Student ID: STU-2024-0047</div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Badge label="Green Belt" bg="rgba(255,255,255,0.2)" color={COLORS.white} />
            <Badge label="Karate" bg="rgba(255,255,255,0.2)" color={COLORS.white} />
          </div>
        </div>

        {/* Contact Info */}
        <SectionTitle>Contact Information</SectionTitle>
        {[
          { icon: '📞', label: 'Phone', value: '+973 3456 7890' },
          { icon: '📧', label: 'Email', value: 'fahad@email.com' },
          { icon: '👤', label: 'Guardian', value: 'Mohammed Al-Dosari' },
          { icon: '📱', label: 'Guardian Phone', value: '+973 3456 1234' },
        ].map((item) => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <span style={{ fontSize: 14, marginRight: 10 }}>{item.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 10, color: COLORS.gray500 }}>{item.label}</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray800 }}>{item.value}</div>
            </div>
          </div>
        ))}

        {/* Notification Settings */}
        <SectionTitle>Notifications</SectionTitle>
        {[
          { label: 'Session Reminders', on: true },
          { label: 'Homework Alerts', on: true },
          { label: 'Payment Reminders', on: true },
          { label: 'Promotional Messages', on: false },
        ].map((n) => (
          <div key={n.label} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${COLORS.gray100}` }}>
            <div style={{ flex: 1, fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray800 }}>{n.label}</div>
            <div style={{ width: 40, height: 22, borderRadius: 11, background: n.on ? COLORS.success : COLORS.gray300, display: 'flex', alignItems: 'center', padding: '0 2px', cursor: 'pointer' }}>
              <div style={{ width: 18, height: 18, borderRadius: 9, background: COLORS.white, marginLeft: n.on ? 18 : 0, transition: 'margin-left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        ))}

        {/* App Info */}
        <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${COLORS.gray100}` }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500 }}>App Version</span>
          <span style={{ fontFamily: FONTS.number, fontSize: 12, color: COLORS.gray400 }}>1.0.0</span>
        </div>

        {/* Logout */}
        <div style={{ padding: '16px' }}>
          <div style={{ background: COLORS.white, border: `1px solid ${COLORS.error}`, color: COLORS.error, borderRadius: 12, padding: '12px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Log Out</div>
        </div>
      </div>
      <BottomNav items={studentNav} active="Me" />
    </PhoneFrame>
  );
}

function AddStudentDialog() {
  return (
    <PhoneFrame>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Background dimmed content */}
        <div style={{ flex: 1, background: COLORS.gray100, opacity: 0.5, padding: '16px' }}>
          <div style={{ height: 12, background: COLORS.gray300, borderRadius: 4, width: '60%', marginBottom: 8 }} />
          <div style={{ height: 10, background: COLORS.gray200, borderRadius: 4, width: '80%', marginBottom: 6 }} />
          <div style={{ height: 10, background: COLORS.gray200, borderRadius: 4, width: '70%', marginBottom: 20 }} />
          <div style={{ height: 40, background: COLORS.gray200, borderRadius: 10, marginBottom: 8 }} />
          <div style={{ height: 40, background: COLORS.gray200, borderRadius: 10, marginBottom: 8 }} />
          <div style={{ height: 40, background: COLORS.gray200, borderRadius: 10 }} />
        </div>

        {/* Bottom Sheet */}
        <div style={{ background: COLORS.white, borderTopLeftRadius: 20, borderTopRightRadius: 20, boxShadow: '0 -4px 24px rgba(0,0,0,0.12)', padding: '10px 16px 16px', maxHeight: '75%', overflowY: 'auto' }}>
          {/* Handle */}
          <div style={{ width: 36, height: 4, borderRadius: 2, background: COLORS.gray300, margin: '0 auto 12px' }} />
          <div style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700, color: COLORS.gray800, marginBottom: 14 }}>Add New Student</div>

          {/* Form Fields */}
          {[
            { label: 'Full Name', placeholder: 'Enter student name' },
            { label: 'Date of Birth', placeholder: 'DD / MM / YYYY', icon: '📅' },
            { label: 'Phone Number', placeholder: '+973 XXXX XXXX' },
            { label: 'Guardian Name', placeholder: 'Parent/Guardian full name' },
            { label: 'Guardian Phone', placeholder: '+973 XXXX XXXX' },
          ].map((f) => (
            <div key={f.label} style={{ marginBottom: 10 }}>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>{f.label}</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray400, display: 'flex', justifyContent: 'space-between' }}>
                <span>{f.placeholder}</span>
                {f.icon && <span>{f.icon}</span>}
              </div>
            </div>
          ))}

          {/* Dropdowns */}
          {[
            { label: 'Discipline', value: 'Karate' },
            { label: 'Coach', value: 'Coach Ahmed' },
            { label: 'Membership Plan', value: 'Monthly Kids - 45 BHD' },
          ].map((d) => (
            <div key={d.label} style={{ marginBottom: 10 }}>
              <label style={{ fontFamily: FONTS.body, fontSize: 10, fontWeight: 600, color: COLORS.gray600, display: 'block', marginBottom: 4 }}>{d.label}</label>
              <div style={{ border: `1px solid ${COLORS.gray300}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, fontFamily: FONTS.body, color: COLORS.gray800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{d.value}</span>
                <span style={{ color: COLORS.gray400 }}>▾</span>
              </div>
            </div>
          ))}

          {/* Save Button */}
          <div style={{ background: COLORS.primary, color: COLORS.white, borderRadius: 12, padding: '13px 0', textAlign: 'center', fontFamily: FONTS.heading, fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 6 }}>Save Student</div>
        </div>
      </div>
    </PhoneFrame>
  );
}

/* ── Named exports (for presentation) ─────────────────── */
export { CoachDashboard, CoachQR, CoachExercise, CoachEarnings, CoachSchedule, CoachLeave, CoachProfile, CoachPayments, StudentLanding, StudentSignup, StudentOTP, StudentLogin, StudentDashboard, ParentView, StudentQR, StudentProgress, StudentExercises, StudentPayments, StudentProfile, AddStudentDialog };

/* ── Screen map ────────────────────────────────────────── */

const SCREEN_MAP = {
  'coach-dashboard': CoachDashboard,
  'coach-qr': CoachQR,
  'coach-exercise': CoachExercise,
  'coach-earnings': CoachEarnings,
  'coach-schedule': CoachSchedule,
  'coach-leave': CoachLeave,
  'coach-profile': CoachProfile,
  'coach-payments': CoachPayments,
  'student-landing': StudentLanding,
  'student-signup': StudentSignup,
  'student-otp': StudentOTP,
  'student-login': StudentLogin,
  'student-dashboard': StudentDashboard,
  'parent-view': ParentView,
  'student-qr': StudentQR,
  'student-progress': StudentProgress,
  'student-exercises': StudentExercises,
  'student-payments': StudentPayments,
  'student-profile': StudentProfile,
  'add-student-dialog': AddStudentDialog,
};

/* ── Main component ────────────────────────────────────── */

export default function DojoMobileWireframes() {
  const [currentScreen, setCurrentScreen] = useState('coach-dashboard');

  const ScreenComponent = SCREEN_MAP[currentScreen];

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, fontFamily: FONTS.body }}>
      {/* Header */}
      <div style={{ background: COLORS.white, borderBottom: `1px solid ${COLORS.gray200}`, padding: '16px 20px 12px', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ fontFamily: FONTS.heading, fontSize: 22, fontWeight: 800, color: COLORS.primary, marginBottom: 2 }}>DojoMaster</div>
        <div style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.gray500, marginBottom: 12 }}>Mobile Wireframes</div>

        {/* Screen selector */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {screens.map((group) => (
            <React.Fragment key={group.group}>
              <span style={{ fontFamily: FONTS.heading, fontSize: 9, fontWeight: 700, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0', width: '100%' }}>{group.group}</span>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setCurrentScreen(item.id)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    cursor: 'pointer',
                    borderRadius: 20,
                    padding: '6px 14px',
                    fontSize: 11,
                    fontFamily: FONTS.body,
                    fontWeight: 600,
                    background: currentScreen === item.id ? COLORS.primary : COLORS.gray200,
                    color: currentScreen === item.id ? COLORS.white : COLORS.gray600,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {item.label}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Screen content */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 20px 48px' }}>
        {ScreenComponent && <ScreenComponent />}
      </div>
    </div>
  );
}
